import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { shopApi } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Filter, X } from "lucide-react";
import { useSeo } from "@/lib/seo";

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const search = params.get("search") || "";
  const featured = params.get("featured");
  const sort = params.get("sort") || "newest";
  const [price, setPrice] = React.useState([0, 500]);

  const { data: categories = [] } = useQuery({ queryKey: ["cats"], queryFn: shopApi.categories });
  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products", category, search, sort, price, featured],
    queryFn: () => shopApi.products({
      category: category || undefined, search: search || undefined,
      sort, min_price: price[0], max_price: price[1],
      featured: featured === "true" ? true : undefined, limit: 60,
    }),
  });

  const setParam = (k, v) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p); };
  const active = category ? categories.find(c => c.slug === category)?.name : (search ? `"${search}"` : "All products");
  useSeo({
    title: category
      ? `${(active || "Shop")} — NovalynxTech`
      : (search ? `Search: ${search} — NovalynxTech` : "Shop — Premium electronics · NovalynxTech"),
    description: category
      ? `Shop ${active?.toLowerCase() || "premium electronics"} at NovalynxTech. Fast shipping, 12-month warranty, 30-day returns.`
      : "Browse premium earbuds, headphones, smartwatches, gaming gear and smart-home accessories. Free shipping over $50, warranty included.",
    canonical: typeof window !== "undefined" ? window.location.origin + "/shop" : "",
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-end justify-between mb-6">
        <div>
          <p className="chip mb-2">Shop</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">{active}</h1>
          <p className="text-sm text-muted-foreground mt-1">{products.length} products</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={sort} onValueChange={(v) => setParam("sort", v)}>
            <SelectTrigger className="w-44 bg-card border-white/10" data-testid="sort-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="price_asc">Price: low to high</SelectItem>
              <SelectItem value="price_desc">Price: high to low</SelectItem>
              <SelectItem value="rating">Top rated</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid md:grid-cols-[240px_1fr] gap-8">
        <aside className="space-y-6" data-testid="filters-panel">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2"><Filter className="h-3 w-3" /> Category</p>
            <div className="space-y-1">
              <button onClick={() => setParam("category", "")} className={`block w-full text-left px-3 py-2 rounded-lg text-sm ${!category ? "bg-primary/20 text-primary" : "hover:bg-secondary"}`} data-testid="filter-all">All</button>
              {categories.map((c) => (
                <button key={c.slug} onClick={() => setParam("category", c.slug)} className={`block w-full text-left px-3 py-2 rounded-lg text-sm ${category === c.slug ? "bg-primary/20 text-primary" : "hover:bg-secondary"}`} data-testid={`filter-${c.slug}`}>{c.name}</button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Price</p>
            <Slider min={0} max={500} step={10} value={price} onValueChange={setPrice} />
            <div className="flex justify-between text-xs text-muted-foreground mt-2"><span>${price[0]}</span><span>${price[1]}+</span></div>
          </div>
          {(category || search || featured) && (
            <button onClick={() => setParams({})} className="text-xs text-primary flex items-center gap-1" data-testid="clear-filters"><X className="h-3 w-3" /> Clear filters</button>
          )}
        </aside>

        <div>
          {isLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">{[...Array(6)].map((_, i) => <div key={i} className="aspect-square bg-card animate-pulse rounded-2xl" />)}</div>
          ) : products.length === 0 ? (
            <p className="text-muted-foreground text-center py-16">No products found. Try clearing filters.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-testid="products-grid">
              {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
